/**
 * Role-based redirect utility for handling user navigation after login
 * 
 * Role mapping based on folder structure:
 * - Role 0 (Super Admin) → Admin Panel (/dashboard/admin) → src/admin/AdminLayout.jsx
 * - Role 1 (User) → User Panel (/dashboard/user) → src/user/UserLayout.jsx - Always, regardless of organization
 * - Role 2 (Org Admin) → Organization Admin Panel (/org-admin) → src/organization/Layout.jsx
 */

/**
 * Redirects user to the appropriate panel based on their role
 * @param {number|string} role - User role (0, 1, or 2)
 * @param {function} navigate - React Router navigate function
 * @param {object} options - Additional options
 * @param {boolean} options.replace - Whether to replace current history entry (default: true)
 * @returns {string} - The redirect path that was navigated to
 */
export const redirectByRole = (role, navigate, options = {}) => {
  const { replace = true } = options;
  
  // Ensure role is a number
  const roleNum = parseInt(role);
  
  let redirectPath;
  
  switch (roleNum) {
    case 0: // Super Admin
      redirectPath = '/admin/dashboard';
      break;
    case 1: // User
      redirectPath = '/user/dashboard';
      break;
    case 2: // Organization Admin
      redirectPath = '/organization/dashboard';
      break;
    default:
      // Custom roles > 2 → Instructor Panel
      if (roleNum > 2) {
        redirectPath = '/instructor/dashboard';
      } else {
        console.warn(`Unknown role: ${role}. Redirecting to home page.`);
        redirectPath = '/';
      }
      break;
  }
  
  // Perform navigation
  navigate(redirectPath, { replace });
  
  return redirectPath;
};

/**
 * Gets the default route path for a given role without navigating
 * @param {number|string} role - User role (0, 1, or 2)
 * @returns {string} - The default route path for the role
 */
export const getDefaultRouteForRole = (role) => {
  const roleNum = parseInt(role);
  
  switch (roleNum) {
    case 0: // Super Admin
      return '/admin/dashboard';
    case 1: // User
      return '/user/dashboard';
    case 2: // Organization Admin
      return '/organization/dashboard';
    default:
      // Custom roles > 2 → Instructor Panel
      return roleNum > 2 ? '/instructor/dashboard' : '/';
  }
};

/**
 * Checks if a user has access to a specific route based on their role
 * @param {number|string} userRole - User's role
 * @param {string} routePath - The route path to check
 * @returns {boolean} - Whether the user has access to the route
 */
export const hasRouteAccess = (userRole, routePath) => {
  const roleNum = parseInt(userRole);
  
  // Super Admin (role 0) has access to all routes
  if (roleNum === 0) {
    return true;
  }
  
  // User (role 1) has access to user routes only
  if (roleNum === 1) {
    return routePath.startsWith('/user') || routePath === '/';
  }
  
  // Org Admin (role 2) has access to org-admin routes only
  if (roleNum === 2) {
    return routePath.startsWith('/organization') || routePath === '/';
  }

  // Instructor (role > 2) has access to instructor routes only
  if (roleNum > 2) {
    return routePath.startsWith('/instructor') || routePath === '/';
  }

  // Unknown role has no access
  return false;
};

/**
 * Gets a human-readable role name
 * @param {number|string} role - User role (0, 1, or 2)
 * @returns {string} - Human-readable role name
 */
export const getRoleName = (role) => {
  const roleNum = parseInt(role);
  
  switch (roleNum) {
    case 0:
      return 'Super Admin';
    case 1:
      return 'User';
    case 2:
      return 'Organization Admin';
    default:
      return 'Unknown Role';
  }
};
